import React, { useState } from "react";
import { useAppDispatch } from "commons/store/hooks";
import ColoredBox from "components/ColoredBox";
import { Table } from "components/Table";
import { useSnackbar } from "notistack";
import { GetApp } from "@mui/icons-material";
import { Box, Button, Chip } from "@mui/material";
import DateTime from "core/helpers/date/DateTime";
import { DATETIME_FORMAT } from "commons/constants/env";
import { useTranslation } from "react-i18next";
import { useQueryEcrfDataManagements } from "utils/ecrfDataManagement";
import { HeaderPanel } from "./HeaderPanel";
import { useMercure } from "utils/mercure";
import { downloadDataExportFile } from "../../../ecrf/redux/ecrfDataManagementSlice";

const DashboardExport = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { isLoading, data: { data: data, hubURL: hubURL } = { hubURL: null } }  = useQueryEcrfDataManagements();
  const collection = useMercure(data, hubURL);
  const items = collection ? (collection["member"] ?? []) : [];

  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  const getColor = (status: string) => {
    if (status === "New") return "secondary";
    else if (status === "Completed") return "success";
    else if (status === "Cancelled" || status === "Error") return "error";
    else if (status === "Rescheduled") return "warning";
    else return "default";
  };

  const columns = () => {
    const columns: any[] = [
      {
        title: <Box ml="20px">{t('type')}</Box>,
        render: (row: any) => (
          <Box>
            {row?.typeName}
          </Box>
        ),
        key: 'type',
      },
      {
        title: <Box ml="20px">{t('name')}</Box>,
        render: (row: any) => (
          <Box>
            {row?.name}
          </Box>
        ),
        key: 'name',
        options: {
          sort: true,
          sortOrder: { name: 'name', direction: 'desc' },
        },
      },
      {
        title: <Box ml="20px">{t('status')}</Box>,
        render: (row: any) => (
          <Box>
            <Chip
              size="small"
              label={t(row?.statusName)}
              color={getColor(row?.statusName || 'Default')}
            />
          </Box>
        ),
        key: 'status',
      },
      {
        title: <Box ml="20px">{t('updated-at')}</Box>,
        render: (row: any) => (
          <Box>
            {DateTime.toIso(row?.updatedAt, DATETIME_FORMAT)}
          </Box>
        ),
        key: 'updatedAt',
      },
    ];

    columns.push({
      title: <Box ml="20px">{t('action')}</Box>,
      render: (row: any) => {
        const handleDownload = async () => {
          setLoadingIds(prev => new Set(prev).add(row.id));
          try {
            await dispatch(downloadDataExportFile(row.id)).unwrap();
          } catch (e) {
            enqueueSnackbar(t('error'), { variant: 'error' });
          } finally {
            setLoadingIds(prev => {
              const next = new Set(prev);
              next.delete(row.id);
              return next;
            });
          }
        };

        return (
          <Box>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={row.status !== 1}
              onClick={handleDownload}
              loading={loadingIds.has(row.id)}
            >
              <GetApp />
            </Button>
          </Box>
        );
      },
      key: 'action',
    });

    return columns;
  }

  return (
    <ColoredBox pt={0} px={5} maxWidth="100%" ml="auto" mr="auto">
      <HeaderPanel/>
      <Table
        pagination={{
          current: page,
          // pageSize: collection?.itemsPerPage || 30,
          pageSize: collection?.totalItems || 30,
          total: collection?.totalItems || 0,
        }}
        onChangePage={(page) => setPage(page)}
        data={items}
        tableName={t('Export data')}
        columns={columns()}
        isLoading={isLoading}
      />
    </ColoredBox>
  );
};

export default DashboardExport;
